/**
 * Mediabunny target helpers for writing to FileSystemWritableFileStream
 * or buffering to memory and saving/uploading after finalize.
 */
import {BufferTarget, StreamTarget, StreamTargetChunk} from 'mediabunny';

type StreamLike = FileSystemWritableFileStream;

export type MbTarget = {
	kind: 'stream' | 'buffer';
	target: StreamTarget | BufferTarget;
	finalize: (result: { buffer?: ArrayBuffer | null }, mime?: string) => Promise<void>;
	cleanup?: () => Promise<void>;
};

/**
 * Create a Mediabunny target for the given handle.
 * - FileSystemFileHandle → StreamTarget (finalize closes it)
 * - string (http/https) → BufferTarget and upload on finalize
 * - string (filename) → prefer streaming to OPFS then download on finalize (fallback: BufferTarget)
 */
export async function createMbTarget(handle: FileSystemFileHandle | string): Promise<MbTarget> {
	if (typeof handle !== 'string') {
		const writable: StreamLike = await handle.createWritable();
		// FileSystemWritableFileStream is compatible with StreamTargetChunk writes.
		// TS types don't model this generically, so cast to the expected WritableStream.
		return {
			kind: 'stream',
			target: new StreamTarget(writable as unknown as WritableStream<StreamTargetChunk>, { chunked: true }),
			finalize: async () => {},
		};
	}

	// Upload target (URL)
	if (/^https?:\/\//i.test(handle)) {
		const target = new BufferTarget();
		return {
			kind: 'buffer',
			target,
			finalize: async ({ buffer }, mime = 'video/mp4') => {
				if (!buffer) throw new Error('No buffer to upload');
				await uploadBuffer(buffer, handle, mime);
			},
		};
	}

	// Local download target (filename) — prefer streaming to OPFS to avoid buffering large files in memory.
	const opfs = await tryCreateOpfsTarget(handle);
	if (opfs) return opfs;

	// Fallback: buffer in memory and download (may OOM for large exports).
	const target = new BufferTarget();
	return {
		kind: 'buffer',
		target,
		finalize: async ({ buffer }, mime = 'video/mp4') => {
			if (!buffer) throw new Error('No buffer to download');
			await downloadBuffer(buffer, handle, mime);
		},
	};
}

async function uploadBuffer(buffer: ArrayBuffer, url: string, contentType: string) {
	const res = await fetch(url, {
		method: 'PUT',
		headers: { 'Content-Type': contentType },
		body: buffer,
	});
	if (!res.ok) {
		throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
	}
}

async function downloadBuffer(buffer: ArrayBuffer, filename: string, mime: string) {
	const blob = new Blob([buffer], { type: mime });
	const url = URL.createObjectURL(blob);
	try {
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	} finally {
		URL.revokeObjectURL(url);
	}
}

function toSafeDownloadName(name: string): string {
	const base = String(name ?? '').split(/[\\/]/).pop() || 'export';
	return base.replace(/[^\w.\- ()]/g, '_').slice(0, 180) || 'export';
}

function adjustExtensionForMime(filename: string, mime?: string): string {
	const name = toSafeDownloadName(filename);
	const dotIdx = name.lastIndexOf('.');
	const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;

	const m = String(mime ?? '').toLowerCase();
	if (m === 'video/webm') return `${base}.webm`;
	if (m === 'video/mp4') return `${base}.mp4`;
	if (m === 'audio/webm') return `${base}.webm`;
	if (m === 'audio/mp4') return `${base}.m4a`;
	return name;
}

async function tryCreateOpfsTarget(downloadName: string): Promise<MbTarget | null> {
	try {
		if (typeof navigator === 'undefined') return null;
		const getDir = (navigator as any)?.storage?.getDirectory;
		if (typeof getDir !== 'function') return null;

		const safeDownloadName = toSafeDownloadName(downloadName);
		const extMatch = safeDownloadName.match(/\.[a-z0-9]+$/i);
		const ext = extMatch ? extMatch[0].toLowerCase() : '';

		// Use a unique staged filename so multiple exports can run concurrently.
		const exportId =
			(globalThis as any)?.crypto?.randomUUID?.() ??
			`export_${Date.now()}_${Math.random().toString(16).slice(2)}`;
		const stagedFileName = `${exportId}${ext || '.bin'}`;

		const root: FileSystemDirectoryHandle = await (navigator as any).storage.getDirectory();
		const exportsDir = await root.getDirectoryHandle('exports', { create: true });
		const fileHandle = await exportsDir.getFileHandle(stagedFileName, { create: true });
		const writable: StreamLike = await fileHandle.createWritable();
		let didCleanup = false;
		const cleanup = async () => {
			if (didCleanup) return;
			didCleanup = true;
			try {
				await (writable as any).close?.();
			} catch {}
			try {
				await exportsDir.removeEntry(stagedFileName);
			} catch {}
		};

		return {
			kind: 'stream',
			target: new StreamTarget(writable as unknown as WritableStream<StreamTargetChunk>, { chunked: true }),
			cleanup,
			finalize: async (_result, mime = 'video/mp4') => {
				// Best-effort: ensure the OPFS stream is closed/flushed.
				try {
					// FileSystemWritableFileStream has a .close() method (in addition to WritableStream closure).
					await (writable as any).close?.();
				} catch {}

				const file = await fileHandle.getFile();
				const type = String(mime || file.type || 'application/octet-stream');
				// Set the MIME type without reading the file into memory.
				const blob = file.slice(0, file.size, type);

				const finalName = adjustExtensionForMime(safeDownloadName, type);
				const url = URL.createObjectURL(blob);
				try {
					const a = document.createElement('a');
					a.href = url;
					a.download = finalName;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
				} finally {
					// Revoke asynchronously to avoid interrupting the download.
					setTimeout(() => URL.revokeObjectURL(url), 60_000);
					// Cleanup the staged OPFS file after the download has had time to start.
					setTimeout(() => {
						cleanup().catch(() => {});
					}, 120_000);
				}
			},
		};
	} catch {
		return null;
	}
}
